import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { getLoyaltyProgramLogs } from "../api/AiAgentApi";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

const ACTION_LABELS = {
  UPDATE_COUNT_COUPON: "Купоны",
  UPDATE_BONUS_BALANCE: "Бонусы",
  UPDATE_COUNT_HOOKAHS: "Кальяны",
  UPDATE_COUNT_USER_GAME: "Игры",
};

const ACTION_COLORS = {
  UPDATE_COUNT_COUPON: "secondary",
  UPDATE_BONUS_BALANCE: "primary",
  UPDATE_COUNT_HOOKAHS: "warning",
  UPDATE_COUNT_USER_GAME: "info",
};

// Маппинг полей UI к константам сортировки на сервере
const SORT_MAPPING = {
  createdAt: "CREATED_AT",
  id: "ID",
  value: "VALUE",
  gameBonus: "GAME_BONUS",
};

export const LoyaltyLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const observer = useRef();
  const lastLogElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Дебаунс поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
      setLogs([]);
      setHasMore(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Сброс при изменении фильтров или сортировки
  useEffect(() => {
    setPage(0);
    setLogs([]);
    setHasMore(true);
  }, [actionFilter, order, orderBy, fromDate, toDate]);

  const fetchLogs = useCallback(async () => {
    if (!hasMore && page !== 0) return;
    
    try {
      setLoading(true);
      const params = {
        page,
        size: rowsPerPage,
        sortBy: SORT_MAPPING[orderBy] || "CREATED_AT",
        direction: order.toUpperCase(),
        action: actionFilter === "all" ? null : actionFilter,
        search: debouncedSearch,
        from: fromDate ? dayjs(fromDate).toISOString() : null,
        to: toDate ? dayjs(toDate).toISOString() : null,
      };
      
      const response = await getLoyaltyProgramLogs(params);
      
      const newLogs = response?.content || [];
      
      setLogs(prev => page === 0 ? newLogs : [...prev, ...newLogs]);
      setHasMore(!response?.last && newLogs.length > 0);
      
    } catch (error) {
      console.error("Failed to fetch loyalty logs:", error);
    } finally {
      setLoading(false);
    }
  }, [page, orderBy, order, actionFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatDateTime = (date) => (date ? dayjs(date).format("DD.MM.YYYY HH:mm:ss") : "—");

  const renderUserInfo = (user) => {
    if (!user) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>;
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Без имени"}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          ID: {user.id}
        </Typography>
      </Box>
    );
  };

  const renderCouponDetails = (coupon) => {
    if (!coupon) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>КУПОН (ID: {coupon.id})</Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption">Тип: {coupon.type?.name} ({coupon.type?.action})</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Описание: {coupon.type?.description || "—"}</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Создан: {formatDateTime(coupon.createdAt)}</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Использован: {formatDateTime(coupon.useAt)}</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Истекает: {formatDateTime(coupon.expiredAt)}</Typography>
        </Stack>
      </Box>
    );
  };

  const renderBonusDetails = (bonus) => {
    if (!bonus) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>БОНУС (ID: {bonus.id})</Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>Истекает: {formatDateTime(bonus.expiredAt)}</Typography>
      </Box>
    );
  };

  const renderGameDetails = (game) => {
    if (!game) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main' }}>ИГРА (ID: {game.id})</Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption">Название: {game.game?.name} (ID: {game.game?.id})</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Бонусы: <b>{game.countBonus || 0}</b></Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>Истекает: {formatDateTime(game.expiredAt)}</Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Логи программы лояльности
      </Typography>

      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              size="small"
              label="Поиск"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />
            <TextField
              select
              size="small"
              label="Тип действия"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            >
              <MenuItem value="all">Все действия</MenuItem>
              {Object.entries(ACTION_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="От"
              type="datetime-local"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="До"
              type="datetime-local"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, maxHeight: 'calc(100vh - 280px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "createdAt" ? order : false}>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Дата
                </TableSortLabel>
              </TableCell>
              <TableCell>Пользователь</TableCell>
              <TableCell>Сотрудник</TableCell>
              <TableCell sortDirection={orderBy === "action" ? order : false}>
                <TableSortLabel
                  active={orderBy === "action"}
                  direction={orderBy === "action" ? order : "asc"}
                  onClick={() => handleRequestSort("action")}
                >
                  Действие
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={orderBy === "value" ? order : false}>
                <TableSortLabel
                  active={orderBy === "value"}
                  direction={orderBy === "value" ? order : "asc"}
                  onClick={() => handleRequestSort("value")}
                >
                  Значение
                </TableSortLabel>
              </TableCell>
              <TableCell>Информация о бонусах / купонах / играх</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log, index) => {
              const isLastElement = logs.length === index + 1;
              return (
                <TableRow 
                  key={`${log.createdAt}-${index}`} 
                  hover 
                  ref={isLastElement ? lastLogElementRef : null}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                  <TableCell>
                    {renderUserInfo(log.user)}
                  </TableCell>
                  <TableCell>
                    {renderUserInfo(log.employee)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={ACTION_LABELS[log.action] || log.action} 
                      color={ACTION_COLORS[log.action] || "default"} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: log.value > 0 ? 'success.main' : log.value < 0 ? 'error.main' : 'text.primary' 
                      }}
                    >
                      {log.value > 0 ? `+${log.value}` : log.value}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    {renderCouponDetails(log.coupon)}
                    {renderBonusDetails(log.bonus)}
                    {renderGameDetails(log.game)}
                    {!log.coupon && !log.bonus && !log.game && (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="text.secondary">Логи не найдены</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
